import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCompany } from '../contexts/CompanyContext';
import { fetchCompanyNotificationTemplates } from '../services/companyCustomizationApi';
import type { CompanyNotificationTemplate } from '../services/companyCustomizationApi';

type TemplateState = {
  templates: CompanyNotificationTemplate[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export const useCompanyNotificationTemplates = (channel?: string): TemplateState => {
  const { currentCompany } = useCompany();
  const [templates, setTemplates] = useState<CompanyNotificationTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const loadTemplates = useCallback(async () => {
    if (!currentCompany?.id || !channel) {
      setTemplates([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchCompanyNotificationTemplates(currentCompany.id, channel);
      setTemplates(data.filter((template) => template.isActive));
    } catch (err) {
      console.error('[useCompanyNotificationTemplates] Failed to load templates', err);
      setError('Não foi possível carregar os templates de notificação.');
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  }, [channel, currentCompany?.id]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates, refreshToken]);

  const refresh = useCallback(async () => {
    setRefreshToken((token) => token + 1);
  }, []);

  return useMemo(
    () => ({
      templates,
      isLoading,
      error,
      refresh,
    }),
    [templates, isLoading, error, refresh],
  );
};


